import { formatDateSimple } from './common';
import {
  IMAGE_DATA_URI,
  InteractionDisplayValue,
  isRecord,
  OMITTED,
  summariseObject,
  text,
} from './interaction-display-value';
import { displayNodeTypeName } from './node-type';

/**
 * The per-type formatters: one function per shape an interaction answer arrives in.
 *
 * Kept apart from `interaction-note-format.ts` so that file stays the dispatcher and
 * the public entry point, and neither grows past the 250-line limit.
 */

/** An allocation node: show the node name, qualified by its type when present. */
export const formatAllocation = (value: Record<string, unknown>): InteractionDisplayValue => {
  const name = typeof value.name === 'string' ? value.name : '';
  if (!name) return summariseObject(value);
  const nodeType = displayNodeTypeName(
    typeof value.nodeTypeName === 'string' ? value.nodeTypeName : undefined
  );
  return text(nodeType ? `${name} (${nodeType})` : name);
};

/**
 * The sentence the desktop UploadField stores once an upload succeeds
 * (`interactionFieldComponents.tsx`), in place of the descriptor mobile stores.
 */
const UPLOAD_SUCCESS_SUFFIX = /\s*successfully uploaded\.?$/i;

/**
 * A bare file name: something with an extension, and no path separators. Used to
 * decide whether the residue of the sentence above is really a file name — so an
 * upload field holding some other string is still shown as that string.
 */
const FILE_NAME = /^[^\s/\\][^/\\]*\.[a-z0-9]{2,5}$/i;

/**
 * An upload answer. Resolves to a `file` reference wherever a file name can be
 * recovered, so the history panel can turn it back into a link; the raw string is kept
 * as text when it is evidently not a file name.
 */
export const formatUpload = (value: unknown, title: string): InteractionDisplayValue => {
  if (typeof value === 'string') {
    if (IMAGE_DATA_URI.test(value)) return { kind: 'image', src: value, alt: title };
    const candidate = value.replace(UPLOAD_SUCCESS_SUFFIX, '').trim();
    return FILE_NAME.test(candidate) ? { kind: 'file', fileName: candidate } : text(value);
  }
  if (!isRecord(value)) return OMITTED;
  const fileName = typeof value.fileName === 'string' ? value.fileName : '';
  if (!fileName) return summariseObject(value);
  const fileKey = typeof value.fileKey === 'string' ? value.fileKey : undefined;
  return { kind: 'file', fileName, fileKey };
};

/** A parts-request selection: the part, plus quantity and condition when set. */
export const formatPartsRequest = (value: unknown): InteractionDisplayValue => {
  if (typeof value === 'string') return text(value);
  if (!isRecord(value)) return OMITTED;
  const name =
    (typeof value.partName === 'string' && value.partName) ||
    (typeof value.name === 'string' && value.name) ||
    '';
  if (!name) return summariseObject(value);
  const parts = [name];
  if (typeof value.quantity === 'number') parts.push(`qty ${value.quantity}`);
  if (typeof value.condition === 'string' && value.condition) parts.push(value.condition);
  return text(parts.join(' · '));
};

/**
 * A fault/solution selection is stored as a JSON *string* by the field that collects
 * it, so it needs parsing before its name can be shown.
 */
export const formatFaultSelection = (value: unknown): InteractionDisplayValue => {
  if (isRecord(value)) return summariseObject(value);
  if (typeof value !== 'string') return OMITTED;
  const raw = value.trim();
  if (!raw.startsWith('{')) return text(raw);
  try {
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) ? summariseObject(parsed) : text(raw);
  } catch {
    return text(raw);
  }
};

export const formatDateValue = (value: unknown): InteractionDisplayValue => {
  if (typeof value !== 'string' || value.trim() === '') return OMITTED;
  const formatted = formatDateSimple(value);
  // formatDateSimple yields "Invalid Date" for anything dayjs can't read; show the
  // original rather than that.
  return text(formatted.includes('Invalid') ? value : formatted);
};
